import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, ChevronRight, ChevronDown } from 'lucide-react';
import { companyInfo } from '../mockData';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
      setServicesOpen(false);
      setMobileServicesOpen(false);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Blog', path: '/blog' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top utility bar */}
      <div className="bg-[#050C1A] border-b border-white/10 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-end items-center h-9 gap-6">
            <a
              href={`tel:${companyInfo.phone.uae}`}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <Phone className="w-3 h-3 text-[#0057B8]" />
              {companyInfo.phone.uae}
            </a>
            <a
              href={`mailto:${companyInfo.email}`}
              className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
            >
              <Mail className="w-3 h-3 text-[#0057B8]" />
              {companyInfo.email}
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav
        className={`transition-all duration-300 ${
          isScrolled
            ? 'bg-[#0A0F1E]/98 backdrop-blur-md [-webkit-backdrop-filter:blur(12px)] shadow-2xl shadow-black/40'
            : 'bg-[#0A0F1E]'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 md:h-[70px]">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <img
                src="/Hexalogix.png"
                alt="Hexalogix Automation Logo"
                className="h-10 w-auto group-hover:opacity-90 transition-all"
              />
              <span className="hidden sm:block text-[#1B7A8C] font-extrabold text-lg tracking-widest whitespace-nowrap uppercase leading-none group-hover:opacity-90 transition-all">
                HEXALOGIX AUTOMATION
              </span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                if (link.name === 'Services') {
                  return (
                    <div
                      key={link.path}
                      className="relative"
                      onMouseEnter={() => setServicesOpen(true)}
                      onMouseLeave={() => setServicesOpen(false)}
                    >
                      <button
                        onClick={() => setServicesOpen((prev) => !prev)}
                        onFocus={() => setServicesOpen(true)}
                        aria-expanded={servicesOpen}
                        className="flex items-center gap-1 px-4 py-2 text-base font-medium tracking-wide text-gray-300 hover:text-white transition-colors rounded-sm group relative"
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${servicesOpen ? 'rotate-180' : ''}`} />
                        <span className={`absolute bottom-0 left-0 h-[2px] bg-[#0057B8] transition-all duration-300 ${isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'}`} />
                      </button>

                      {/* Services Dropdown Menu */}
                      {servicesOpen && (
                        <div className="absolute left-0 top-full pt-2 w-56 z-50">
                          <div className="bg-white shadow-lg rounded-md py-2">
                          <Link
                            to="/services/plc"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            PLC Programming
                          </Link>
                          <Link
                            to="/services/scada"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            SCADA Systems
                          </Link>
                          <Link
                            to="/services/bms"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            BMS Solutions
                          </Link>
                          <Link
                            to="/services/control-panels"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            Control Panel Manufacturing
                          </Link>
                          <Link
                            to="/services/testing-commissioning"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            Testing & Commissioning
                          </Link>
                          <Link
                            to="/services/turnkey"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base"
                          >
                            Turnkey Solutions
                          </Link>
                          <hr className="my-2" />
                          <Link
                            to="/services"
                            onClick={() => setServicesOpen(false)}
                            className="block px-4 py-2 text-gray-800 hover:bg-blue-50 hover:text-[#0057B8] transition-colors text-base font-semibold"
                          >
                            View All Services
                          </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`relative px-4 py-2 text-base font-medium tracking-wide transition-colors rounded-sm group ${
                      isActive(link.path)
                        ? 'text-white'
                        : 'text-gray-300 hover:text-white'
                    }`}
                  >
                    {link.name}
                    <span
                      className={`absolute bottom-0 left-0 h-[2px] bg-[#0057B8] transition-all duration-300 ${
                        isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                      }`}
                    />
                  </Link>
                );
              })}
            </div>

            {/* CTA Button */}
            <div className="hidden md:flex items-center gap-3">
              <Link to="/contact">
                <button className="flex items-center gap-2 bg-[#0057B8] hover:bg-[#0066CC] text-white text-base font-semibold px-5 py-2.5 transition-all duration-200 hover:shadow-lg hover:shadow-[#0057B8]/30 group">
                  Talk to an Engineer
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-gray-300 hover:text-white hover:bg-white/10 transition-colors rounded"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-[#050C1A] max-h-[calc(100vh-4rem)] overflow-y-auto">
            <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link) => {
                if (link.name === 'Services') {
                  return (
                    <div key={link.path}>
                      <button
                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                        className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-gray-300 hover:text-white hover:bg-white/5 rounded transition-colors"
                      >
                        {link.name}
                        <ChevronDown className={`w-4 h-4 transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {/* Mobile Services Submenu */}
                      {mobileServicesOpen && (
                        <div className="bg-white/5 border-l-2 border-[#0057B8] flex flex-col gap-0">
                          <Link
                            to="/services/plc"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            PLC Programming
                          </Link>
                          <Link
                            to="/services/scada"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            SCADA Systems
                          </Link>
                          <Link
                            to="/services/bms"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            BMS Solutions
                          </Link>
                          <Link
                            to="/services/control-panels"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            Control Panel Manufacturing
                          </Link>
                          <Link
                            to="/services/testing-commissioning"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            Testing & Commissioning
                          </Link>
                          <Link
                            to="/services/turnkey"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-medium text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            Turnkey Solutions
                          </Link>
                          <div className="my-1 border-t border-white/10" />
                          <Link
                            to="/services"
                            onClick={() => {
                              setMobileServicesOpen(false);
                              setIsMobileMenuOpen(false);
                            }}
                            className="px-8 py-2 text-sm font-semibold text-gray-400 hover:text-white hover:bg-white/5 transition-colors leading-snug"
                          >
                            View All Services
                          </Link>
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`px-4 py-3 text-base font-medium rounded transition-colors ${
                      isActive(link.path)
                        ? 'text-white bg-[#0057B8]/20 border-l-2 border-[#0057B8]'
                        : 'text-gray-300 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {link.name}
                  </Link>
                );
              })}
              <Link
                to="/contact"
                onClick={() => setIsMobileMenuOpen(false)}
                className="mt-3 flex items-center justify-center gap-2 bg-[#0057B8] hover:bg-[#0066CC] text-white text-base font-semibold px-5 py-3 transition-colors"
              >
                Talk to an Engineer
                <ChevronRight className="w-4 h-4" />
              </Link>
              <div className="mt-3 pt-3 border-t border-white/10 flex flex-col gap-2">
                <a href={`tel:${companyInfo.phone.uae}`} className="flex items-center gap-2 text-sm text-gray-400">
                  <Phone className="w-3 h-3 text-[#0057B8]" />
                  {companyInfo.phone.uae}
                </a>
                <a href={`mailto:${companyInfo.email}`} className="flex items-start gap-2 text-sm text-gray-400 min-w-0">
                  <Mail className="w-3 h-3 text-[#0057B8] flex-shrink-0 mt-0.5" />
                  <span className="break-all">{companyInfo.email}</span>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
